import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/Icon";
import { ReaderContext } from "@PublicationReader/context";
import { PublicationI, SectionI } from "@PublicationReader/@types/Publication";

interface SectionProps {
  section: SectionI;
  level: number;
}

const Section = ({ section, level }: SectionProps) => {
  const { activeSectionId } = React.useContext(ReaderContext);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const goToSection = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    sectionId: string,
  ) => {
    event.preventDefault();

    const section = document.getElementById(sectionId);
    if (section) {
      window.scroll({
        top: section.offsetTop - 50,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const hasSubSections =
    section.subSections && section.subSections.length > 0 ? true : false;

  return (
    <li
      className={classNames(
        "pubr-toc__list-item",
        level > 1 && `pubr-toc__list-item--level-${level}`,
      )}
    >
      <div className="pubr-toc__list-item-content">
        <a
          href="#"
          className={classNames(
            "pubr-toc__link",
            activeSectionId === section.sectionId && "pubr-toc__link--active",
          )}
          onClick={(event) => goToSection(event, section.sectionId)}
        >
          {section.sectionHeading}
        </a>
        {hasSubSections && (
          <button
            className="pubr-toc__toggle"
            onClick={() => {
              setIsExpanded((prev) => !prev);
            }}
            aria-expanded={isExpanded}
            aria-controls={section.sectionId}
          >
            <Icon name={isExpanded ? "minus" : "plus"} />
          </button>
        )}
      </div>

      {hasSubSections && (
        <>
          <ul
            className={classNames(
              "pubr-toc__list pubr-toc__list--sub",
              isExpanded && "pubr-toc__list--expanded",
            )}
          >
            {mapSections(section.subSections!, level + 1)}
          </ul>
        </>
      )}
    </li>
  );
};

const mapSections = (sections: SectionI[], level: number) => {
  return (
    <>
      {sections.map((section) => (
        <Section section={section} level={level} key={section.sectionId} />
      ))}
    </>
  );
};

const TableOfContents = ({ sections }: Pick<PublicationI, "sections">) => {
  const { t } = useTranslation("common");
  const ref = React.useRef<HTMLDivElement>(null);
  const { tocIsOpen, setTocIsOpen } = React.useContext(ReaderContext);
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (closeBtnRef?.current) {
      closeBtnRef.current.focus();
    }
  }, [tocIsOpen]);

  return (
    <nav
      className={classNames("pubr-toc", tocIsOpen && "pubr-toc--open")}
      ref={ref}
    >
      <div className="pubr-toc__header">
        <button
          ref={closeBtnRef}
          onClick={() => setTocIsOpen(false)}
          aria-label={t("closeToc") as string}
          aria-controls="pubr-toc"
          aria-expanded={tocIsOpen}
          className="pubr-icon-button pubr-icon-button--nav"
        >
          <Icon name="cross" />
        </button>
      </div>
      <div className="pubr-toc__inner">
        <ul className="pubr-toc__list">{mapSections(sections, 1)}</ul>
      </div>
    </nav>
  );
};

export { TableOfContents };
